import {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';

import { convertDates } from '@opositandopn/http';
import { Splash, sleep, useToasterActions } from '@opositandopn/ui';
import axios, { AxiosError, AxiosInstance } from 'axios';

import { useAppActions } from '../state/appStore';
import { useAuth } from './AuthContext';
import { useConfiguration } from './ConfigurationContext';
import { usePwa } from './PwaContext';
import { createResponseErrorHandler } from './responseErrorHandler';

export type AxiosProps = {
  instance: AxiosInstance;
};

export const AxiosContext = createContext<AxiosProps | null>(null);

export const AxiosContextProvider = ({ children }: PropsWithChildren) => {
  const { configuration } = useConfiguration();
  const { user } = useAuth();
  const { setNeedUpdate } = usePwa();
  const { sendMessage } = useToasterActions();
  const { setShowGetPremiumPopup, setShowQuotaExceededPopup } = useAppActions();

  const [axiosInstance, setAxiosInstance] = useState<AxiosInstance>();

  useEffect(() => {
    if (user === null) {
      return;
    }

    const instance = axios.create({
      baseURL: configuration.app?.apiUrl,
    });

    const req = instance.interceptors.request.use(
      async (config) => {
        config.headers.Authorization = `Bearer ${await user.getIdToken()}`;
        config.headers.Accept = 'application/json';
        config.headers['Content-Type'] = 'application/json';
        config.headers['Version'] = '1.0';

        return config;
      },
      // Returned, not merely constructed: without the return the rejection is
      // dropped and the interceptor resolves with undefined, so a request that
      // failed before it was ever sent continues as though it had a config.
      (error: AxiosError) => Promise.reject(error),
    );

    instance.defaults.validateStatus = (status: number) => status < 400;

    const res = instance.interceptors.response.use(
      (response) => {
        const xAppVersion = response.headers['x-app-version'];

        if (
          xAppVersion !== undefined &&
          import.meta.env.VITE_VERSION_MAJOR !== undefined &&
          import.meta.env.VITE_VERSION_MINOR !== undefined
        ) {
          const major = +xAppVersion.split('.')[0];
          const minor = +xAppVersion.split('.')[1];

          if (
            major > +import.meta.env.VITE_VERSION_MAJOR ||
            (major === +import.meta.env.VITE_VERSION_MAJOR &&
              minor > +import.meta.env.VITE_VERSION_MINOR)
          ) {
            sleep(5000).then(() => {
              setNeedUpdate(true);
            });
          } else {
            setNeedUpdate(false);
          }
        }

        if (response.data) {
          response.data = convertDates(response.data);
        }

        return response;
      },
      createResponseErrorHandler({
        instance,
        getIdToken: (forceRefresh) => user.getIdToken(forceRefresh),
        sendMessage,
        setShowGetPremiumPopup,
        setShowQuotaExceededPopup,
      }),
    );

    setAxiosInstance(() => instance);

    return () => {
      instance.interceptors.request.eject(req);
      instance.interceptors.response.eject(res);
    };
  }, [
    configuration,
    sendMessage,
    setNeedUpdate,
    setShowGetPremiumPopup,
    setShowQuotaExceededPopup,
    user,
  ]);

  if (axiosInstance === undefined) {
    return <Splash />;
  }

  return (
    <AxiosContext.Provider value={{ instance: axiosInstance }}>
      {children}
    </AxiosContext.Provider>
  );
};

export const useAxios = () => {
  const context = useContext(AxiosContext);

  if (!context) {
    throw new Error('useAxios must be used within a AxiosContextProvider');
  }

  return context;
};
